import { AdaaHelper } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import { AdaaBoolean } from "../../../../../../constants/enums";
import type { UserGrantModelType, UserPermissionModelType } from "../../../../../../models";
import { commonFormatter, SharedFields } from "../../configs";
import type {
  AccordionSectionConfigType,
  ListSectionConfigType,
  ScreenSectionConfigType,
  ScreenType,
} from "../../wf-difference.types";

const ENABLER_PERMISSION_TYPE = "Enabler Pillars";
const WTUAE_PERMISSION_TYPE = "We The UAE Pillars";

const isPmoUser = (data: { entityId: number }) => data.entityId === Constants.CONSTANT_PMO_ID;

const usernameField: ScreenSectionConfigType = {
  field: "username",
  label: "common.form.label.username",
  view: "normal",
};

const emailField: ScreenSectionConfigType = {
  field: "email",
  label: "common.form.label.email",
  view: "normal",
};

const jobTitleAEField: ScreenSectionConfigType = {
  field: "jobTitleAE",
  label: "common.form.label.jobTitleAE",
  view: "normal",
};

const jobTitleENField: ScreenSectionConfigType = {
  field: "jobTitleEN",
  label: "common.form.label.jobTitleEN",
  view: "normal",
};

const directPhoneField: ScreenSectionConfigType = {
  field: "directPhone",
  label: "common.form.label.directPhone",
  view: "normal",
};

const mobilePhoneField: ScreenSectionConfigType = {
  field: "mobilePhone",
  label: "common.form.label.mobilePhone",
  view: "normal",
};

const ministerField: ScreenSectionConfigType = {
  field: "minister",
  label: "user.create.minister",
  view: "normal",
  format: (data: { minister: boolean }) => commonFormatter.matchTrueFalse(data.minister),
};

const directorGeneralField: ScreenSectionConfigType = {
  field: "directorGeneral",
  label: "user.create.director_general",
  view: "normal",
  format: (data: { directorGeneral: boolean }) => commonFormatter.matchTrueFalse(data.directorGeneral),
};

const entityField: ScreenSectionConfigType = {
  ...SharedFields.entityIdField,
  label: "common.form.label.entity",
};

const positionsField: ListSectionConfigType = {
  field: "orgUnits",
  label: "common.form.label.position",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
};

const selectedPermissionsOfType = (itemType: string) => (list: unknown) =>
  (list as UserPermissionModelType[]).filter((e) => e.itemTypeEN === itemType && e.views === AdaaBoolean.Y);

const enablerPermissionsField: ListSectionConfigType = {
  field: "userPermissions",
  label: "user.create.enabler_permissions",
  view: "list",
  idKey: "itemId",
  derivedList: selectedPermissionsOfType(ENABLER_PERMISSION_TYPE),
  visibleIf: isPmoUser,
  format: ($o) => AdaaHelper.getItemValueByToken($o, "itemName"),
};

const wtuaePermissionsField: ListSectionConfigType = {
  field: "userPermissions",
  label: "user.create.wtuae_permissions",
  view: "list",
  idKey: "itemId",
  derivedList: selectedPermissionsOfType(WTUAE_PERMISSION_TYPE),
  visibleIf: isPmoUser,
  format: ($o) => AdaaHelper.getItemValueByToken($o, "itemName"),
};

const adaa4Field: ScreenSectionConfigType = {
  field: "adaa4",
  label: "user.create.adaa4_login",
  view: "normal",
  format: (data: { adaa4: boolean }) => commonFormatter.matchTrueFalse(data.adaa4),
};

type RoleGrantsType = {
  roleId: number;
  roleNameEN: string;
  roleNameAE: string;
  entities: { grantKey: string; nameEN: string; nameAE: string }[];
};

const grantKey = (grant: UserGrantModelType) => `${grant.entityId}-${grant.roleId}-${grant.orgUnitId ?? ""}`;

const grantName = (grant: UserGrantModelType, lang: "EN" | "AE") =>
  [grant[`entityName${lang}`], grant[`orgUnitName${lang}`]]
    .filter((e) => AdaaHelper.isDefinedAndNotEmpty(e))
    .join(" / ");

const groupGrantsByRole = (list: unknown) => {
  const roles = new Map<number, RoleGrantsType>();

  for (const grant of list as UserGrantModelType[]) {
    const role = roles.get(grant.roleId) ?? {
      roleId: grant.roleId,
      roleNameEN: grant.roleNameEN,
      roleNameAE: grant.roleNameAE,
      entities: [],
    };

    role.entities.push({ grantKey: grantKey(grant), nameEN: grantName(grant, "EN"), nameAE: grantName(grant, "AE") });
    roles.set(grant.roleId, role);
  }

  for (const role of roles.values()) {
    role.entities.sort((a, b) => a.grantKey.localeCompare(b.grantKey));
  }

  return [...roles.values()];
};

const roleEntitiesField: ListSectionConfigType = {
  field: "entities",
  label: "user.create.entity",
  view: "list",
  idKey: "grantKey",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
};

const grantsField: AccordionSectionConfigType = {
  field: "grants",
  label: "user.create.roles",
  view: "accordion",
  idKey: "roleId",
  derivedList: groupGrantsByRole,
  title: (role) => `${AdaaHelper.getItemValueByToken(role, "roleName")} (${(role as RoleGrantsType).entities.length})`,
  config: [roleEntitiesField],
  hasDiff: ($new, $old) => {
    const grantKeys = (role: unknown) => (role as RoleGrantsType).entities.map((e) => e.grantKey).join();
    return grantKeys($new) !== grantKeys($old);
  },
};

const userGroupsField: ListSectionConfigType = {
  field: "userGroups",
  label: "user.create.groups",
  view: "list",
  idKey: "id",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
};

const entitiesResponsibleForField: ListSectionConfigType = {
  field: "entitiesResponsibleFor",
  label: "user.create.responsibleFor",
  view: "list",
  idKey: "entityId",
  format: ($o) => AdaaHelper.getItemValueByToken($o, "entityName"),
};

export const userScreenConfig = {
  type: "simple" as ScreenType,
  sections: [
    usernameField,
    emailField,
    ...SharedFields.name,
    jobTitleAEField,
    jobTitleENField,
    directPhoneField,
    mobilePhoneField,
    ministerField,
    directorGeneralField,
    entityField,
    positionsField,
    enablerPermissionsField,
    wtuaePermissionsField,
    adaa4Field,
    userGroupsField,
    grantsField,
    entitiesResponsibleForField,
  ],
};
